import React from "react";
import {
  TFunction,
  Trans,
  useTranslation,
  UseTranslationOptions,
} from "react-i18next";
import { LxI18NResource, LxTranslationHook } from "../LxI18N.model";
import { useLxI18NProvider } from "./LxI18N.context";

export function translationHook<K extends string | number | symbol>(
  resources: LxI18NResource[]
): (options?: UseTranslationOptions) => LxTranslationHook<K> {
  return function useLxTranslationHook(options?: UseTranslationOptions) {
    const { status, ready: readyPromise, testMode } = useLxI18NProvider();
    const namespaces = React.useMemo(
      () => resources.map((r) => r.namespace),
      []
    );

    const getResource = React.useCallback(
      (key: string) =>
        resources.find((r) => Object.keys(r.translations).includes(key)),
      []
    );

    const getFullQualifiedKey = React.useCallback(
      (key: string) => {
        const res = getResource(key);
        const ret = res ? `${res.namespace}:${key}` : key;
        return ret;
      },
      [getResource]
    );

    const { i18n, ready, t: _t } = useTranslation(namespaces, options);

    const LxTrans = React.useCallback(
      (props: any) => {
        const { i18nKey, ...other } = props;
        const translationKey = getFullQualifiedKey(i18nKey);

        /**
         * Triggers the React.suspense if not ready, but not in test-mode since it masks the intended behavior - in this case, revert
         * to the previous behavior
         */
        if (status !== "ready" && !testMode) {
          throw new Promise((resolve, reject) => {
            readyPromise
              .then(() => resolve(null))
              .catch((e) => {
                console.error(e);
                reject(e);
              });
          });
        }

        return <Trans i18nKey={translationKey} {...other} />;
      },
      [status, readyPromise, testMode, getFullQualifiedKey]
    );

    const t: TFunction<string[], undefined> = React.useCallback(
      (key: string, options: any) => {
        const translationKey = getFullQualifiedKey(key);
        return _t(translationKey, {
          ns: getResource(key)?.namespace,
          ...options,
        });
      },
      [_t, getFullQualifiedKey, getResource]
    );

    return { t, i18n, ready, LxTrans };
  };
}
